import type { TKindleChaptersInfo, TKindleCreationData } from '@/types/kindle-books.ts'

// Splits HTML into per-chapter strings, delimited by the first heading tag found
export function splitHtmlIntoChapters(html: string): string[] {
  const container = document.createElement('div')
  container.innerHTML = html
  const firstHeading = container.querySelector('h1,h2,h3,h4,h5,h6')

  if (!firstHeading) {
    return [html]
  }

  const chapterTag = firstHeading.tagName
  const chapterHtmlStrings: string[] = []
  let currentChapterDiv: HTMLDivElement | null = null

  for (const child of Array.from(container.children)) {
    if (child.tagName === chapterTag) {
      currentChapterDiv = document.createElement('div')
      chapterHtmlStrings.push('')
    }

    if (currentChapterDiv) {
      currentChapterDiv.appendChild(child.cloneNode(true))
      chapterHtmlStrings[chapterHtmlStrings.length - 1] = currentChapterDiv.innerHTML
    }
  }

  return chapterHtmlStrings.length > 0 ? chapterHtmlStrings : [html]
}

// Maps a navigator click to a device scroll value. rawElementBottom (image bottom or click Y for text)
// drives snap-to-end so large images are handled correctly regardless of click position within them.
export function calcNavClickScrollY(
  rawClickY: number,
  maxScroll: number,
  contentWindowHeight: number,
  rawClickedElementBottom: number,
): number {
  if (rawClickY <= contentWindowHeight / 2) {
    return 0
  }

  if (rawClickedElementBottom >= maxScroll) {
    return maxScroll
  }

  return rawClickY - contentWindowHeight / 2
}

export function clearTipTapContentForPreview(html?: string | null): string | null {
  if (!html) {
    console.log('No draft HTML content')
    return null
  }

  const newBody: HTMLBodyElement = document.createElement('body')
  newBody.innerHTML = html
  removeTipTapStuff(newBody)
  markChapterHeadings(newBody)

  return newBody.outerHTML
}

export function tipTapDocToEpubData(html?: string | null): TKindleCreationData | null {
  if (!html) {
    console.log('No draft HTML content')
    return null
  }

  const newBody: HTMLBodyElement = document.createElement('body')
  newBody.innerHTML = html

  removeTipTapStuff(newBody)
  removeEmptyHeaders(newBody)
  removeEmptyBlockElements(newBody)

  const imgUrls: string[] = handleAndGetImages(newBody)
  const chaptersInfo: TKindleChaptersInfo = getChaptersInfo(newBody)

  return {
    node: newBody,
    images: imgUrls,
    chapters: chaptersInfo.chapters,
    chapterNames: chaptersInfo.chapterNames,
  }
}

function getChaptersInfo(element: HTMLElement): TKindleChaptersInfo {
  const firstHeading: HTMLElement | null = element.querySelector('h1,h2,h3,h4,h5,h6')

  if (!firstHeading) {
    const chapter: HTMLDivElement = document.createElement('div')
    chapter.className = 'chapter'

    while (element.firstChild) {
      chapter.appendChild(element.firstChild)
    }

    return { chapters: [chapter], chapterNames: ['Article'] }
  }

  const chapterTag = firstHeading.tagName
  return splitContentInChapters(element, chapterTag)
}

function splitContentInChapters(element: HTMLElement, tag: string): TKindleChaptersInfo {
  const result: TKindleChaptersInfo = {
    chapters: [],
    chapterNames: [],
  }

  let currentChapter: HTMLDivElement | null = null

  Array.from(element.childNodes).forEach((node) => {
    if (node.nodeType === 1 && (node as HTMLElement).matches(tag)) {
      currentChapter = document.createElement('div')
      currentChapter.className = 'chapter'

      result.chapters.push(currentChapter)

      const header = node as HTMLElement
      result.chapterNames.push(header.textContent?.trim() || '')

      currentChapter.appendChild(header)
    } else if (currentChapter) {
      currentChapter.appendChild(node)
    }
  })

  return result
}
function markChapterHeadings(element: HTMLElement): void {
  const firstHeading = element.querySelector('h1,h2,h3,h4,h5,h6')
  if (!firstHeading) {
    return
  }

  element.querySelectorAll<HTMLElement>(firstHeading.tagName).forEach((h) => {
    h.classList.add('chapter-heading')
  })
}

function removeTipTapStuff(element: HTMLElement): void {
  element.querySelectorAll<HTMLElement>('.ai-images-wrapper[data-images]').forEach((el) => el.remove())

  const pagesDiv: HTMLElement | null = element.querySelector('#pages')
  if (pagesDiv) {
    pagesDiv.remove()
  }

  element.querySelectorAll<HTMLElement>('*').forEach((el) => {
    Object.keys(el.dataset).forEach((key) => delete el.dataset[key])
  })
}

function removeEmptyHeaders(element: HTMLElement) {
  const headers: NodeListOf<HTMLElement> = element.querySelectorAll('h1, h2, h3, h4, h5, h6')

  headers.forEach((header) => {
    if (header.textContent.trim() === '') {
      header.remove()
    }
  })
}

function removeEmptyBlockElements(element: HTMLElement) {
  element.querySelectorAll('br').forEach((br) => br.remove())

  const blocks: NodeListOf<HTMLElement> = element.querySelectorAll('p, div')
  blocks.forEach((block) => {
    if (block.textContent.trim() === '' && block.querySelector('img') === null) {
      block.remove()
    }
  })
}

function handleAndGetImages(element: HTMLElement): string[] {
  const imageUrls: string[] = []
  const images: NodeListOf<HTMLImageElement> = element.querySelectorAll('img')

  images.forEach((image): void => {
    image.removeAttribute('dir')
    image.removeAttribute('contenteditable')
    image.removeAttribute('draggable')
    image.removeAttribute('style')
    image.setAttribute('alt', 'Image')
    image.setAttribute('width', '75%')
    image.style.display = 'block'
    image.style.marginLeft = 'auto'
    image.style.marginRight = 'auto'

    const src: string | null = image?.src
    if (!src) {
      image.remove()
    } else if (imageUrls.indexOf(src) < 0) {
      imageUrls.push(src)
    }
  })

  return imageUrls
}
